import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { getToken } from '../auth/auth';
import AppDownloadModal from './AppDownloadModal';
import { APP_SCHEME } from '../legal/appLinks';

const API = 'https://maineventbackend.seuytel.com/api/';
const BACKEND_ROOT = 'https://maineventbackend.seuytel.com';
const STORAGE = `${BACKEND_ROOT}/storage/`;
const UA = typeof navigator !== 'undefined' ? navigator.userAgent || '' : '';
const IS_ANDROID = /Android/i.test(UA);
const IS_IOS =
    /iPhone|iPad|iPod/i.test(UA) ||
    (typeof navigator !== 'undefined' &&
        (navigator as any).platform === 'MacIntel' &&
        (navigator as any).maxTouchPoints > 1);

type PaymentMethodType = 'card' | 'klarna' | 'apple_pay' | 'google_pay';

function paymentMethodLabel(key: PaymentMethodType, label: string) {
    if (key !== 'google_pay') return label;

    return (
        <span style={styles.googlePayMethod}>
            <span style={styles.googlePayMark}>
                <span style={{ color: '#4285F4' }}>G</span>
                <span> Pay</span>
            </span>
            <span>{label}</span>
        </span>
    );
}

interface Ticket {
    id: number;
    title?: string;
    description?: string | null;
    price: number;
    currency?: string;
    img_url?: string | null;
    ticket_status?: number;
    current_amount?: number | null;
    max_amount?: number | null;
    max_simultaneous_purchase?: number | null;
    allow_multiple_purchase?: boolean | null;
    seat_sections?: any[];
    is_standing_block?: boolean;
    is_seat_autoselect_block?: boolean;
    is_reselling?: boolean;
    is_resaling?: boolean;
    sales_start?: string | null;
    sales_end?: string | null;
}

interface EventData {
    id?: number;
    title?: string;
    currency?: string;
    seat_map_id?: number | null;
    seat_map?: any[];
    story?: { survey?: any[] };
}

function calcFeePerTicket(price: number): number {
    if (price < 25) return price * 0.15;
    if (price <= 50) return price * 0.10;
    return price * 0.05;
}

function ticketImageUrl(path?: string | null): string {
    if (!path) return '';
    return /^https?:/i.test(path) ? path : `${STORAGE}${path}`;
}

function numberValue(value: unknown, fallback = 0): number {
    const n = Number(value ?? fallback);
    return Number.isFinite(n) ? n : fallback;
}

function formatMoney(amount: number, currency = 'EUR'): string {
    return `${currency} ${amount.toFixed(2)}`;
}

function isVenueSeatTicket(ticket: Ticket): boolean {
    return Array.isArray(ticket.seat_sections)
        && ticket.seat_sections.some((entry: any) => !!entry?.section_id && !entry?.Table_name && !entry?.table_name);
}

export default function EventTicketsComponent() {
    const navigate = useNavigate();
    const { slug = '' } = useParams();
    const [searchParams] = useSearchParams();
    const eventIdParam = searchParams.get('eventId') || (/^\d+$/.test(slug) ? slug : '');
    const eventIndex = searchParams.get('eventIndex') || '0';

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [event, setEvent] = useState<EventData>({});
    const [tickets, setTickets] = useState<Ticket[]>([]);
    const [quantities, setQuantities] = useState<Record<number, number>>({});
    const [paymentMethod, setPaymentMethod] = useState<PaymentMethodType>('card');
    const [surveyAnswers, setSurveyAnswers] = useState<Record<number, boolean>>({});

    useEffect(() => {
        let cancelled = false;
        async function load() {
            if (!eventIdParam) {
                setError('Event ID missing.');
                setLoading(false);
                return;
            }

            setLoading(true);
            setError('');
            try {
                const [ticketRes, eventRes] = await Promise.all([
                    fetch(`${API}tickets/getOneEventTickets/${encodeURIComponent(eventIdParam)}/${encodeURIComponent(eventIndex)}`),
                    fetch(`${API}events/getMyEvent-with-tickets/${encodeURIComponent(eventIdParam)}`),
                ]);
                if (!ticketRes.ok) throw new Error(`Tickets could not be loaded (${ticketRes.status}).`);
                const ticketJson = await ticketRes.json();
                const eventJson = eventRes.ok ? await eventRes.json() : null;
                if (cancelled) return;

                setTickets(Array.isArray(ticketJson.data)
                    ? ticketJson.data.map((ticket: any) => ({
                        ...ticket,
                        price: numberValue(ticket.price),
                    }))
                    : []);
                setEvent(eventJson?.data ?? { id: Number(eventIdParam) });
            } catch (err: any) {
                if (!cancelled) setError(err?.message || 'Something went wrong.');
            } finally {
                if (!cancelled) setLoading(false);
            }
        }

        load();
        return () => {
            cancelled = true;
        };
    }, [eventIdParam, eventIndex]);

    const visibleTickets = useMemo(
        () => tickets.filter(ticket => !(ticket.is_reselling ?? ticket.is_resaling) && ticket.ticket_status !== 0),
        [tickets],
    );

    const eventHasSeatMap = !!event.seat_map_id;

    const selectedLines = useMemo(() => visibleTickets
        .map(ticket => ({ ticket, quantity: quantities[ticket.id] || 0 }))
        .filter(line => line.quantity > 0), [visibleTickets, quantities]);

    const subtotal = selectedLines.reduce((sum, line) => sum + line.ticket.price * line.quantity, 0);
    const platformFee = selectedLines.reduce((sum, line) => sum + calcFeePerTicket(line.ticket.price) * line.quantity, 0);
    const grandTotal = subtotal + platformFee;
    const currency = selectedLines[0]?.ticket.currency || event.currency || visibleTickets[0]?.currency || 'EUR';
    const paymentMethods = useMemo(() => [
        ['card', 'Card'],
        ['klarna', 'Klarna'],
        ...(IS_ANDROID ? [['google_pay', 'Google Pay']] : []),
        ...(IS_IOS ? [['apple_pay', 'Apple Pay']] : []),
    ] as Array<[PaymentMethodType, string]>, []);

    const surveyPayload = useMemo(() => {
        const survey = event.story?.survey || [];
        return survey.map((question: any, index: number) => ({
            question: typeof question === 'string'
                ? question
                : (question.checkable ?? question.question ?? question.text ?? String(question)),
            answer: surveyAnswers[index] ?? false,
        }));
    }, [event.story, surveyAnswers]);

    const ensureTokenOrLogin = (): string | null => {
        const token = getToken();
        if (token) return token;
        const redirect = `${window.location.pathname}${window.location.search}${window.location.hash}`;
        navigate(`/login?redirect=${encodeURIComponent(redirect)}`);
        return null;
    };

    const maxForTicket = (ticket: Ticket): number => {
        const remaining = Math.max(0, numberValue(ticket.max_amount) - numberValue(ticket.current_amount));
        const maxSimul = numberValue(ticket.max_simultaneous_purchase, 10) || 10;
        return Math.max(0, Math.min(remaining || maxSimul, maxSimul));
    };

    const setTicketQuantity = (ticket: Ticket, nextQuantity: number) => {
        const max = maxForTicket(ticket);
        const next = Math.max(0, Math.min(max, nextQuantity));
        setQuantities(prev => ({ ...prev, [ticket.id]: next }));
    };

    const openSeatMap = (ticket: Ticket) => {
        if (!ensureTokenOrLogin()) return;
        const params = new URLSearchParams({
            eventId: String(eventIdParam),
            ticketId: String(ticket.id),
            checkout: '1',
            payment_method_type: paymentMethod,
        });
        navigate(`/selection?${params.toString()}`);
    };

    const openCheckout = () => {
        const token = ensureTokenOrLogin();
        if (!token) return;
        if (selectedLines.length === 0) return;
        if (grandTotal < 0.5) {
            alert('Amount must be at least 0.50.');
            return;
        }

        const params = new URLSearchParams({
            token,
            tickets: JSON.stringify(selectedLines.map(line => ({
                ticket_id: line.ticket.id,
                quantity: line.quantity,
                is_standing: !!line.ticket.is_standing_block,
                seats: [],
            }))),
            amount: String(grandTotal),
            currency,
            description: selectedLines.map(line => `${line.ticket.title || 'Ticket'} x${line.quantity}`).join(', '),
            promotion_id: '',
            survey_answers: JSON.stringify(surveyPayload),
            is_donation: 'false',
            donation_message: '',
            mode: 'web',
            payment_method: '2',
            payment_method_type: paymentMethod,
            platform_fee: String(platformFee),
        });
        if (eventIdParam) params.set('event_id', eventIdParam);
        navigate(`/checkout?${params.toString()}`);
    };

    if (loading) {
        return <div style={styles.center}>Loading tickets...</div>;
    }

    if (error) {
        return <div style={styles.centerError}>{error}</div>;
    }

    return (
        <main style={styles.page}>
            {/* "Use the app" interstitial — slides up on every visit */}
            <AppDownloadModal deepLink={event.id ? `${APP_SCHEME}event/${event.id}` : undefined} />

            <section style={styles.header}>
                <button style={styles.backBtn} onClick={() => navigate(-1)}>‹</button>
                <div>
                    <p style={styles.eyebrow}>Tickets</p>
                    <h1 style={styles.title}>{event.title || 'Select tickets'}</h1>
                </div>
            </section>

            <section style={styles.list}>
                {visibleTickets.length === 0 ? (
                    <p style={styles.empty}>No tickets available.</p>
                ) : visibleTickets.map(ticket => {
                    const usesSeatMap = eventHasSeatMap && isVenueSeatTicket(ticket);
                    const qty = quantities[ticket.id] || 0;
                    const max = maxForTicket(ticket);
                    return (
                        <article key={ticket.id} style={styles.ticketCard}>
                            {ticket.img_url ? (
                                <img src={ticketImageUrl(ticket.img_url)} alt="" style={styles.ticketImage} />
                            ) : (
                                <div style={styles.ticketImageFallback}>Ticket</div>
                            )}
                            <div style={styles.ticketBody}>
                                <div style={styles.ticketTop}>
                                    <div>
                                        <h2 style={styles.ticketTitle}>{ticket.title || 'Ticket'}</h2>
                                        {!!ticket.description && <p style={styles.ticketDesc}>{ticket.description}</p>}
                                    </div>
                                    <strong style={styles.price}>{formatMoney(ticket.price, ticket.currency || currency)}</strong>
                                </div>
                                <div style={styles.ticketFoot}>
                                    <span style={styles.remaining}>{max} available</span>
                                    {usesSeatMap ? (
                                        <button style={styles.primarySmall} onClick={() => openSeatMap(ticket)}>
                                            Select seats
                                        </button>
                                    ) : (
                                        <div style={styles.stepper}>
                                            <button style={styles.stepBtn} onClick={() => setTicketQuantity(ticket, qty - 1)} disabled={qty <= 0}>-</button>
                                            <span style={styles.qty}>{qty}</span>
                                            <button style={styles.stepBtn} onClick={() => setTicketQuantity(ticket, qty + 1)} disabled={qty >= max}>+</button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </article>
                    );
                })}
            </section>

            {!!event.story?.survey?.length && (
                <section style={styles.survey}>
                    <h2 style={styles.sectionTitle}>Event survey</h2>
                    {event.story.survey.map((question: any, index: number) => (
                        <label key={index} style={styles.surveyRow}>
                            <input
                                type="checkbox"
                                checked={!!surveyAnswers[index]}
                                onChange={e => setSurveyAnswers(prev => ({ ...prev, [index]: e.target.checked }))}
                            />
                            <span>{typeof question === 'string' ? question : (question.checkable ?? question.question ?? question.text ?? String(question))}</span>
                        </label>
                    ))}
                </section>
            )}

            <section style={styles.payment}>
                <h2 style={styles.sectionTitle}>Payment method</h2>
                <div style={styles.methodGrid}>
                    {paymentMethods.map(([key, label]) => (
                        <button
                            key={key}
                            style={{
                                ...styles.methodBtn,
                                ...(paymentMethod === key ? styles.methodBtnActive : {}),
                            }}
                            onClick={() => setPaymentMethod(key)}
                        >
                            {paymentMethodLabel(key, label)}
                        </button>
                    ))}
                </div>
            </section>

            <div style={styles.payBar}>
                <div>
                    <span style={styles.payLabel}>Total incl. fees</span>
                    <strong style={styles.payTotal}>{formatMoney(grandTotal, currency)}</strong>
                    <span style={styles.feeLabel}>incl. {formatMoney(platformFee, currency)} fees</span>
                </div>
                <button
                    style={{ ...styles.payBtn, ...(selectedLines.length === 0 ? styles.payBtnDisabled : {}) }}
                    onClick={openCheckout}
                    disabled={selectedLines.length === 0}
                >
                    Pay now
                </button>
            </div>
        </main>
    );
}

const styles: Record<string, React.CSSProperties> = {
    page: {
        minHeight: '100vh',
        background: '#F6F7FB',
        padding: '18px 16px 120px',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        color: '#1F2430',
    },
    center: { minHeight: '60vh', display: 'grid', placeItems: 'center', color: '#5B8FE8' },
    centerError: { minHeight: '60vh', display: 'grid', placeItems: 'center', color: '#e05252' },
    header: { maxWidth: 820, margin: '0 auto 16px', display: 'flex', alignItems: 'center', gap: 12 },
    backBtn: { width: 40, height: 40, borderRadius: 20, border: 0, background: '#E7EEFC', color: '#5B8FE8', fontSize: 28, cursor: 'pointer' },
    eyebrow: { margin: 0, color: '#5B8FE8', fontSize: 12, fontWeight: 700, textTransform: 'uppercase' },
    title: { margin: '2px 0 0', fontSize: 24, lineHeight: 1.2 },
    list: { maxWidth: 820, margin: '0 auto', display: 'grid', gap: 12 },
    empty: { color: '#707EAE', textAlign: 'center' },
    ticketCard: { display: 'flex', gap: 12, background: '#fff', border: '1px solid #E4E7F1', borderRadius: 8, padding: 12 },
    ticketImage: { width: 78, height: 78, borderRadius: 8, objectFit: 'cover', flex: '0 0 auto' },
    ticketImageFallback: { width: 78, height: 78, borderRadius: 8, background: '#E7EEFC', color: '#5B8FE8', display: 'grid', placeItems: 'center', fontSize: 12, fontWeight: 700, flex: '0 0 auto' },
    ticketBody: { flex: 1, minWidth: 0 },
    ticketTop: { display: 'flex', justifyContent: 'space-between', gap: 12 },
    ticketTitle: { margin: 0, fontSize: 15, lineHeight: 1.25 },
    ticketDesc: { margin: '6px 0 0', color: '#707EAE', fontSize: 12, lineHeight: 1.4 },
    price: { color: '#1a73e8', whiteSpace: 'nowrap' },
    ticketFoot: { marginTop: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 },
    remaining: { color: '#707EAE', fontSize: 12 },
    stepper: { display: 'flex', alignItems: 'center', background: '#E7EEFC', borderRadius: 8, overflow: 'hidden' },
    stepBtn: { width: 36, height: 34, border: 0, background: '#DCE8FF', color: '#1F2430', cursor: 'pointer', fontSize: 18 },
    qty: { minWidth: 34, textAlign: 'center', fontWeight: 700 },
    primarySmall: { border: 0, borderRadius: 8, background: '#5B8FE8', color: '#fff', padding: '9px 12px', fontWeight: 700, cursor: 'pointer' },
    survey: { maxWidth: 820, margin: '14px auto 0', background: '#fff', border: '1px solid #E4E7F1', borderRadius: 8, padding: 14 },
    sectionTitle: { margin: '0 0 10px', fontSize: 14 },
    surveyRow: { display: 'flex', gap: 8, alignItems: 'center', marginTop: 8, color: '#1F2430', fontSize: 13 },
    payment: { maxWidth: 820, margin: '14px auto 0', background: '#fff', border: '1px solid #E4E7F1', borderRadius: 8, padding: 14 },
    methodGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 8 },
    methodBtn: { border: '1px solid #D6DEF3', borderRadius: 8, background: '#fff', color: '#1F2430', padding: '10px 8px', fontWeight: 700, cursor: 'pointer' },
    methodBtnActive: { borderColor: '#5B8FE8', background: '#E7EEFC', color: '#345FBF' },
    googlePayMethod: { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8 },
    googlePayMark: { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: 48, minHeight: 22, padding: '0 8px', border: '1px solid #DADCE0', borderRadius: 4, background: '#fff', color: '#3C4043', fontSize: 13, fontWeight: 700 },
    payBar: { position: 'fixed', left: 0, right: 0, bottom: 0, background: '#0F172A', color: '#fff', padding: '14px 18px', display: 'flex', justifyContent: 'center', gap: 24, alignItems: 'center', boxShadow: '0 -8px 30px rgba(15,23,42,0.22)' },
    payLabel: { display: 'block', fontSize: 11, color: 'rgba(255,255,255,0.72)' },
    payTotal: { display: 'block', fontSize: 20 },
    feeLabel: { display: 'block', fontSize: 10, color: 'rgba(255,255,255,0.65)' },
    payBtn: { minWidth: 132, border: 0, borderRadius: 8, background: '#5B8FE8', color: '#fff', padding: '13px 18px', fontWeight: 800, cursor: 'pointer' },
    payBtnDisabled: { opacity: 0.45, cursor: 'not-allowed' },
};
